"use client";

import { createPortal } from "react-dom";
import Image from "next/image";
import { IoCloseSharp } from "react-icons/io5";
import { useMixPanel } from "@/app/context/mixPanelContext";

const AINA_INTRO_MODAL_CONFIG = {
  title: "Meet AINA",
  subtitle: "Your personal AI concierge",
  description:
    "Your next-gen AI concierge that isn’t just smart but also intuitive, turning every interaction with customers seamless.",

  sections: [
    {
      id: "what-is-aina",
      title: "What is AINA",
      type: "text",
      content:
        "AINA is an AI-powered benefits concierge that helps customers discover, redeem, and manage their card benefits effortlessly.",
      image: "/common-images/aina_what_is.png",
    },
    {
      id: "how-to-book",
      title: "How to book with AINA",
      type: "list",
      content: [
        "Tell AINA what you want",
        "AINA handles the checks",
        "You confirm, AINA books",
      ],
      image: "/common-images/aina_how_to_book.png",
    },
    {
      id: "what-can-i-do",
      title: "What can I do with AINA",
      type: "list",
      content: [
        "Redeem your benefits",
        "Manage benefits",
        "Know more about your benefits and quota",
      ],
      image: "/common-images/aina_what_can_i_do.png",
    },
  ],

  primaryCta: {
    label: "Get Started",
    event: "chatbot_intro_get_started",
  },

  secondaryCta: {
    label: "Maybe later",
    event: "chatbot_intro_dismissed",
  },
};

type AinaIntroModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onPrimaryAction: () => void;
};

const renderSectionContent = (section: any) => {
  if (section.type === "list") {
    return (
      <ol>
        {section.content.map((item: string, idx: number) => (
          <li key={idx}>{item}</li>
        ))}
      </ol>
    );
  }

  return <p>{section.content}</p>;
};

export default function AinaIntroModal({
  isOpen,
  onClose,
  onPrimaryAction,
}: AinaIntroModalProps) {
  const { trackEvent } = useMixPanel();
  if (!isOpen) return null;

  return createPortal(
    <div className="aina-intro-modal">
      <div
        className="aina-intro-modal__backdrop"
        // onClick={onClose}
      />

      <div className="aina-intro-modal__card">
        <button className="aina-intro-modal__close" onClick={onClose}>
          <IoCloseSharp />
        </button>

        <div className="aina-intro-modal__header">
          <div className="aina-intro-modal__header__title">
            <h2 className="aina-intro-modal__title">
              {AINA_INTRO_MODAL_CONFIG.title}
            </h2>
            <div className="aina-intro-modal__subtitle">
              {AINA_INTRO_MODAL_CONFIG.subtitle}
            </div>
          </div>
          <div className="aina-intro-modal__desc">
            {AINA_INTRO_MODAL_CONFIG.description}
          </div>
        </div>

        <div className="aina-intro-modal__sections">
          {AINA_INTRO_MODAL_CONFIG.sections.map((section) => (
            <div key={section.id} className="aina-intro-modal__section">
              <Image
                src={section.image}
                alt={section.title}
                width={70}
                height={70}
              />
              <div className="aina-intro-modal__section__content">
                <h4>{section.title}</h4>
                {renderSectionContent(section)}
              </div>
            </div>
          ))}
        </div>

        <div className="aina-intro-modal__ctas">
          <button
            className="aina-intro-modal__primary"
            onClick={() => {
              trackEvent?.(AINA_INTRO_MODAL_CONFIG.primaryCta.event);
              onPrimaryAction();
            }}
          >
            {AINA_INTRO_MODAL_CONFIG.primaryCta.label}
          </button>

          <button
            className="aina-intro-modal__secondary"
            onClick={() => {
              trackEvent?.(AINA_INTRO_MODAL_CONFIG.secondaryCta.event);
              onClose();
            }}
          >
            {AINA_INTRO_MODAL_CONFIG.secondaryCta.label}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
